/*
  STORE CUSTOMIZATION — Heat Slide Cart controller.

  Loaded with `defer` from sections/slide-cart.liquid. Everything Liquid knows
  (money format, section settings, box parent handles, $100 freebie ids) is
  handed over in the JSON block `[data-heat-slidecart-config]` inside the
  drawer root. Keep this file plain ES5: it ships to every page.

  Read the comment header in sections/slide-cart.liquid for the safety rails
  (HSOTM bail-out, class prefix, textContent-only rendering, header bubble).

  Ordering rule: every cart mutation made from inside the drawer runs through
  the single promise queue below, so responses can never render out of order
  and a second click cannot race a first (see CLAUDE.md: never fan out
  parallel cart calls). A click during an in-flight request is queued, not
  dropped, and quantity tasks read the cart again when they run.
*/
(function () {
  'use strict';

  var CART_RE = /\/cart\/(add|change|update|clear)\.js/;

  /* ---- one-time network observation (survives section re-renders) ---- */
  if (!window.__heatSlideCartNetHooked) {
    window.__heatSlideCartNetHooked = true;

    var origFetch = window.fetch;
    window.fetch = function (input) {
      var url = '';
      if (typeof input === 'string') {
        url = input;
      } else if (typeof URL !== 'undefined' && input instanceof URL) {
        url = input.href;
      } else {
        url = (input && input.url) || '';
      }
      var promise = origFetch.apply(this, arguments);
      var match = String(url).match(CART_RE);
      if (match) {
        promise.then(function (response) {
          if (response.ok) {
            document.dispatchEvent(new CustomEvent('heat:slidecart:cart-request', {
              detail: { action: match[1] }
            }));
          }
        }).catch(function () {});
      }
      return promise;
    };

    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      var match = String(url || '').match(CART_RE);
      this.__heatSlideCartAction = match ? match[1] : null;
      return origOpen.apply(this, arguments);
    };
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if (this.__heatSlideCartAction) {
        var action = this.__heatSlideCartAction;
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) {
            document.dispatchEvent(new CustomEvent('heat:slidecart:cart-request', {
              detail: { action: action }
            }));
          }
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  /* ---- gesture tracking (auto-open only after real user interaction) ---- */
  if (!window.__heatSlideCartGestureHooked) {
    window.__heatSlideCartGestureHooked = true;
    window.__heatSlideCartLastGesture = 0;
    ['pointerdown', 'keydown'].forEach(function (type) {
      document.addEventListener(type, function () {
        window.__heatSlideCartLastGesture = Date.now();
      }, { capture: true, passive: true });
    });
  }

  /* Section id of the instance currently mounted, so the theme-editor
     load/unload listeners below can tell our section from any other. */
  var currentSectionId = null;

  function readConfig(root) {
    var node = root.querySelector('[data-heat-slidecart-config]');
    var parsed = null;
    if (node) {
      try {
        parsed = JSON.parse(node.textContent);
      } catch (e) {
        parsed = null;
      }
    }
    if (!parsed) parsed = {};
    if (!parsed.boxParentHandles) parsed.boxParentHandles = [];
    if (!parsed.freebie100ProductIds) parsed.freebie100ProductIds = [];
    if (!parsed.gestureWindowMs) parsed.gestureWindowMs = 5000;
    if (!parsed.upsellExcludeTypes) parsed.upsellExcludeTypes = ['Freebie', 'Gift Card', 'Subscription'];
    if (!parsed.upsellFreeShipTypes) parsed.upsellFreeShipTypes = ['Gift Set', 'Bundle'];
    if (!parsed.upsellFreeShipTag) parsed.upsellFreeShipTag = 'giftfreeship';
    if (!parsed.upsellMaxPrice) parsed.upsellMaxPrice = 1500;
    if (!parsed.upsellSeedCount) parsed.upsellSeedCount = 3;
    if (!parsed.upsellHeading) parsed.upsellHeading = 'Recommended Products';
    if (!parsed.upsellHeadingFreeShip) parsed.upsellHeadingFreeShip = parsed.upsellHeading;
    if (!parsed.upsellHeadingFreeShipIntl) parsed.upsellHeadingFreeShipIntl = parsed.upsellHeading;
    if (!parsed.upsellHomeCountry) parsed.upsellHomeCountry = 'US';
    return parsed;
  }

  /* Dedup across theme-editor section re-renders. init() re-parents the live
     root to <body>, so after a re-render the freshly rendered markup is the
     one still sitting inside its `#shopify-section-*` wrapper — prefer it,
     and drop every other root. */
  function pickRoot() {
    var roots = document.querySelectorAll('[data-heat-slidecart-root]');
    if (!roots.length) return null;
    var chosen = roots[roots.length - 1];
    var i;
    for (i = 0; i < roots.length; i++) {
      if (roots[i].closest && roots[i].closest('[id^="shopify-section"]')) chosen = roots[i];
    }
    for (i = 0; i < roots.length; i++) {
      if (roots[i] !== chosen) roots[i].remove();
    }
    return chosen;
  }

  function init() {
    /* Never run on the HSOTM subscription page — its wizard owns the cart
       flow and redirects straight to checkout. */
    if (document.querySelector('.landing_product_subscription')) return;
    if (!window.HeatCart) return;

    /* Tear down a previous instance (theme-editor section re-render) so
       document-level listeners don't stack. */
    if (window.__heatSlideCart && window.__heatSlideCart.destroy) {
      window.__heatSlideCart.destroy();
    }

    var root = pickRoot();
    if (!root) return;

    var config = readConfig(root);
    if (config.sectionId != null) currentSectionId = String(config.sectionId);

    /* Re-parent to <body> so position:fixed can't be broken by a transformed
       ancestor. */
    if (root.parentElement !== document.body) document.body.appendChild(root);

    /* Enable open/close transitions one frame after init, once the hidden
       resting state has painted (see the .is-ready CSS gate in the section). */
    requestAnimationFrame(function () { root.classList.add('is-ready'); });

    var listeners = [];
    function on(target, type, handler) {
      target.addEventListener(type, handler);
      listeners.push([target, type, handler]);
    }
    function onDoc(type, handler) { on(document, type, handler); }

    var panel = root.querySelector('[data-slidecart]');
    var overlay = root.querySelector('[data-slidecart-overlay]');
    var closeBtn = root.querySelector('[data-slidecart-close]');
    var linesEl = root.querySelector('[data-slidecart-lines]');
    var emptyEl = root.querySelector('[data-slidecart-empty]');
    var footerEl = root.querySelector('[data-slidecart-footer]');
    var subtotalEl = root.querySelector('[data-slidecart-subtotal]');
    var shipbarEl = root.querySelector('[data-slidecart-shipbar]');
    var shipbarStatus = root.querySelector('[data-slidecart-shipbar-status]');
    var shipbarProgress = root.querySelector('[data-slidecart-shipbar-progress]');
    var announceEl = root.querySelector('[data-slidecart-announce]');
    var errorEl = root.querySelector('[data-slidecart-error]');
    var noteWrap = root.querySelector('[data-slidecart-note]');
    var noteToggle = root.querySelector('[data-slidecart-note-toggle]');
    var noteBody = root.querySelector('[data-slidecart-note-body]');
    var noteField = root.querySelector('[data-slidecart-note-field]');
    var noteStatus = root.querySelector('[data-slidecart-note-status]');
    var noteLabel = root.querySelector('[data-slidecart-note-label]');
    var noteHint = root.querySelector('[data-slidecart-note-hint]');
    var checkoutForm = root.querySelector('.heat-slidecart__checkout-form');
    /* Holiday shipping deadline line (snippets/custom-holiday-deadline.liquid,
       assets/heat-deadlines.js). This file only says whether the cart is
       eligible; HeatDeadlines decides the season and the wording. */
    var deadlineEl = root.querySelector('[data-heat-deadline][data-hd-surface="cart"]');
    var noteTimer = null;
    /* true from the shopper's first keystroke until saveNote() takes the
       value: render() must not overwrite text that is not saved yet */
    var noteEdited = false;

    /* gift options: no gift markup when gift_options_enabled is off, and no
       packaging fieldset when gift_packaging_prompt_enabled is off (then the
       drawer never reads or writes the Gift packaging attribute) */
    var giftEl = root.querySelector('[data-slidecart-gift]');
    var packEl = root.querySelector('[data-slidecart-pack]');
    var packInputs = root.querySelectorAll('[data-slidecart-pack-input]');
    var nudgeEl = root.querySelector('[data-slidecart-gift-nudge]');
    var packPending = 0;        /* packaging writes queued or in flight */
    var packUserChoice = null;  /* radio the shopper just picked, until saved */
    var packFailedValue = null; /* auto write that failed: never retried in a loop */

    var isOpen = false;
    var lastFocused = null;
    var internalMutations = 0;
    var refreshTimer = null;
    var currentCart = null;
    var cartHasCappedPack = false;
    var pendingOpen = false;

    /* ---------- serialized cart work ----------
       One promise chain for every cart call the drawer makes, so responses can
       never render out of order and two calls can never be in flight at once.
       Clicks are QUEUED, never dropped: a tap that lands while a request is in
       flight becomes the next task, and quantity tasks recompute their target
       from the cart state at execution time (see mutateLineBy). Reads (a plain
       /cart.js refresh) join the same chain. Never enqueue from inside a queued
       task: the new task waits on the current one and deadlocks.

       While the chain is draining, linesEl carries aria-busy="true" (assistive
       tech only, no pointer-events consequence) and the individual line being
       mutated carries data-busy for the dimming. */
    var queue = Promise.resolve();
    var busyDepth = 0;

    /* ---------- the drawer's error line ----------
       Every failure shows through showError(), so a reason from an earlier
       failure never lingers under a later one. A 422 from Shopify carries a
       readable reason ("You can only add 3 of ... to the cart."): mutations
       that can hit a stock limit pass it through failWith(), anything else
       (and any other status) gets the line's own text from the section
       markup. */
    var errorDefault = errorEl.textContent;

    function showError(message) {
      errorEl.textContent = message || errorDefault;
      errorEl.hidden = false;
    }

    function cartErrorText(body) {
      if (!body) return '';
      var text = typeof body.description === 'string' ? body.description : '';
      /* "Cart Error" is the generic title, not a reason */
      if (!text && typeof body.message === 'string' && body.message !== 'Cart Error') text = body.message;
      return text.trim();
    }

    /* Rejects from a non-OK response, carrying Shopify's reason
       (error.cartMessage) only for a 422, the cart's own refusal (stock limit
       and the like). Any other status gets the default text. */
    function failWith(response, label) {
      var error = new Error(label);
      if (response.status !== 422) return Promise.reject(error);
      return response.json().catch(function () { return null; }).then(function (body) {
        error.cartMessage = cartErrorText(body);
        throw error;
      });
    }

    function syncBusy() {
      if (!linesEl) return;
      if (busyDepth > 0) {
        linesEl.setAttribute('aria-busy', 'true');
      } else {
        linesEl.removeAttribute('aria-busy');
      }
    }

    function run(fn, busy) {
      if (busy) {
        busyDepth++;
        syncBusy();
      }
      function done() {
        if (busy) {
          busyDepth--;
          syncBusy();
        }
      }
      function task() {
        return Promise.resolve().then(fn).then(done, done);
      }
      queue = queue.then(task, task);
      return queue;
    }

    function enqueue(fn) { return run(fn, true); }
    function enqueueRead(fn) { return run(fn, false); }

    /* ---------- money ---------- */
    function formatMoney(cents) {
      /* fallback built by concatenation — a literal double brace would end the
         Liquid tag when this string used to live in the section */
      var format = config.moneyFormat || ('$' + '{' + '{amount}' + '}');
      var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
      var key = match ? match[1] : 'amount';
      var value = cents / 100;
      var out;
      switch (key) {
        case 'amount_no_decimals':
          out = Math.round(value).toLocaleString('en-US');
          break;
        case 'amount_with_comma_separator':
          out = value.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
          break;
        case 'amount_no_decimals_with_comma_separator':
          out = Math.round(value).toLocaleString('de-DE');
          break;
        default:
          out = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return format.replace(/\{\{\s*\w+\s*\}\}/, out);
    }

    /* ---------- rendering ---------- */
    var TRASH_SVG = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>';

    function el(tag, className, text) {
      var node = document.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = text;
      return node;
    }

    function imageUrl(src, width) {
      if (!src) return '';
      return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
    }

    function renderLine(item) {
      var li = el('li', 'heat-slidecart__line');
      li.setAttribute('data-line-key', item.key);

      var media = el('div', 'heat-slidecart__line-media');
      if (item.image) {
        var img = document.createElement('img');
        img.src = imageUrl(item.image, 224);
        img.alt = '';
        img.width = 112;
        img.height = 112;
        img.loading = 'lazy';
        media.appendChild(img);
      }
      li.appendChild(media);

      var info = el('div', 'heat-slidecart__line-info');
      var title = el('a', 'heat-slidecart__line-title', item.product_title);
      title.href = item.url;
      info.appendChild(title);

      if (item.variant_title && item.variant_title !== 'Default Title') {
        info.appendChild(el('span', 'heat-slidecart__line-variant', item.variant_title));
      }
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        info.appendChild(el('span', 'heat-slidecart__line-plan', item.selling_plan_allocation.selling_plan.name));
      }
      var props = item.properties || {};
      Object.keys(props).forEach(function (key) {
        if (key.charAt(0) === '_' || props[key] == null || props[key] === '') return;
        info.appendChild(el('span', 'heat-slidecart__line-prop', key + ': ' + props[key]));
      });
      (item.line_level_discount_allocations || []).forEach(function (alloc) {
        if (alloc.discount_application && alloc.discount_application.title) {
          info.appendChild(el('span', 'heat-slidecart__line-discount', alloc.discount_application.title));
        }
      });

      /* A $100-tier freebie that did NOT zero out next to a capped pack:
         explain instead of silently charging (see cart-item.liquid note). */
      if (
        !(item.line_level_discount_allocations || []).length &&
        config.freebie100ProductIds.indexOf(item.product_id) !== -1 &&
        cartHasCappedPack
      ) {
        info.appendChild(el(
          'span',
          'heat-slidecart__line-note',
          'Your pack already includes its freebie, so this item is not free on this order.'
        ));
      }

      var controls = el('div', 'heat-slidecart__line-controls');
      var qty = el('div', 'heat-slidecart__qty');
      var minus = el('button', null, '−');
      minus.type = 'button';
      minus.setAttribute('data-qty-change', '-1');
      minus.setAttribute('aria-label', 'Decrease quantity of ' + item.product_title);
      var qtyValue = el('span', 'heat-slidecart__qty-value', String(item.quantity));
      qtyValue.setAttribute('aria-live', 'polite');
      var plus = el('button', null, '+');
      plus.type = 'button';
      plus.setAttribute('data-qty-change', '1');
      plus.setAttribute('aria-label', 'Increase quantity of ' + item.product_title);
      qty.appendChild(minus);
      qty.appendChild(qtyValue);
      qty.appendChild(plus);
      controls.appendChild(qty);

      var remove = el('button', 'heat-slidecart__line-remove');
      remove.type = 'button';
      remove.setAttribute('data-line-remove', '');
      remove.setAttribute('aria-label', 'Remove ' + item.product_title + ' from cart');
      remove.innerHTML = TRASH_SVG; /* static trusted markup */
      controls.appendChild(remove);

      info.appendChild(controls);
      li.appendChild(info);

      var price = el('div', 'heat-slidecart__line-price');
      if (item.original_line_price > item.final_line_price) {
        price.appendChild(el('s', null, formatMoney(item.original_line_price)));
      }
      price.appendChild(document.createTextNode(formatMoney(item.final_line_price)));
      li.appendChild(price);

      return li;
    }

    /* ---------- Build Your Own Gift Box groups ---------- */
    function renderBoxMember(item) {
      var li = el('li', 'heat-slidecart__box-member');
      li.setAttribute('data-line-key', item.key);

      var media = el('div', 'heat-slidecart__box-member-media');
      if (item.image) {
        var img = document.createElement('img');
        img.src = imageUrl(item.image, 96);
        img.alt = '';
        img.width = 48;
        img.height = 48;
        img.loading = 'lazy';
        media.appendChild(img);
      }
      li.appendChild(media);

      var info = el('div', 'heat-slidecart__box-member-info');
      var title = el('a', 'heat-slidecart__box-member-title', item.product_title);
      title.href = item.url;
      info.appendChild(title);
      var props = item.properties || {};
      if (props._box_mystery) {
        info.appendChild(el('span', 'heat-slidecart__box-member-free', 'Free mystery bottle'));
      }
      if (item.quantity > 1) {
        info.appendChild(el('span', 'heat-slidecart__box-member-qty', '× ' + item.quantity));
      }
      li.appendChild(info);

      /* Marie Sharp's pack bottles are included in the pack's one price, not
         "free" — no struck price, no FREE label (owner call 2026-08-08). The
         group header carries the only price. Gift-box members keep theirs. */
      if (props._box_kind !== 'ms12') {
        var price = el('div', 'heat-slidecart__box-member-price');
        if (item.original_line_price > item.final_line_price) {
          price.appendChild(el('s', null, formatMoney(item.original_line_price)));
          price.appendChild(document.createTextNode(' '));
        }
        price.appendChild(document.createTextNode(
          item.final_line_price === 0 ? 'FREE' : formatMoney(item.final_line_price)
        ));
        li.appendChild(price);
      }

      return li;
    }

    function renderBoxGroup(members) {
      var parent = null;
      var bottles = [];
      var total = 0;
      var keys = [];
      members.forEach(function (item) {
        total += item.final_line_price;
        keys.push(item.key);
        if (config.boxParentHandles.indexOf(item.handle) !== -1) {
          parent = item;
        } else {
          bottles.push(item);
        }
      });

      var li = el('li', 'heat-slidecart__line heat-slidecart__box');
      li.setAttribute('data-box-keys', keys.join('|'));

      var head = el('div', 'heat-slidecart__box-head');

      var media = el('div', 'heat-slidecart__line-media');
      if (parent && parent.image) {
        var img = document.createElement('img');
        img.src = imageUrl(parent.image, 224);
        img.alt = '';
        img.width = 112;
        img.height = 112;
        img.loading = 'lazy';
        media.appendChild(img);
      }
      head.appendChild(media);

      var info = el('div', 'heat-slidecart__line-info');
      var titleText = parent
        ? parent.product_title + (parent.variant_title && parent.variant_title !== 'Default Title' &&
            (parent.properties || {})._box_kind !== 'ms12' ? ' - ' + parent.variant_title : '')
        : 'Your box';
      var title = el('span', 'heat-slidecart__line-title', titleText);
      info.appendChild(title);
      var count = 0;
      bottles.forEach(function (item) { count += item.quantity; });
      info.appendChild(el('span', 'heat-slidecart__line-variant', count + ' bottle' + (count === 1 ? '' : 's')));

      var remove = el('button', 'heat-slidecart__box-remove', 'Remove box');
      remove.type = 'button';
      remove.setAttribute('data-box-remove', '');
      remove.setAttribute('aria-label', 'Remove this box and all its bottles from cart');
      info.appendChild(remove);
      head.appendChild(info);

      var price = el('div', 'heat-slidecart__line-price', formatMoney(total));
      head.appendChild(price);

      li.appendChild(head);

      var list = el('ul', 'heat-slidecart__box-members');
      bottles.forEach(function (item) {
        list.appendChild(renderBoxMember(item));
      });
      li.appendChild(list);

      return li;
    }

    function render(cart) {
      currentCart = cart;
      if (!cart) return;

      /* True when the cart holds a build-your-own pack parent priced at or
         past the $100 freebie tier (its qualifying spend is capped at $99.99
         by the sauce-freebies function). */
      cartHasCappedPack = (cart.items || []).some(function (i) {
        var p = i.properties || {};
        return p._box_kind === 'ms12' && p._box_parent && i.final_price >= 10000;
      });

      linesEl.textContent = '';
      /* group Build-Your-Own-Gift-Box lines (shared _box_id) at the position
         of each group's first line; everything else renders as before */
      var groups = {};
      var entries = [];
      cart.items.forEach(function (item) {
        var props = item.properties || {};
        var boxId = props._box_id;
        if (!boxId) {
          entries.push({ item: item });
          return;
        }
        if (!groups[boxId]) {
          groups[boxId] = [];
          entries.push({ box: groups[boxId] });
        }
        groups[boxId].push(item);
      });
      entries.forEach(function (entry) {
        linesEl.appendChild(entry.box ? renderBoxGroup(entry.box) : renderLine(entry.item));
      });

      var isEmpty = cart.item_count === 0;
      emptyEl.hidden = !isEmpty;
      footerEl.hidden = isEmpty;
      subtotalEl.textContent = formatMoney(cart.items_subtotal_price);

      if (noteWrap) {
        noteWrap.hidden = isEmpty;
        /* Unsaved typing survives a render: any other queued write (a packaging
           choice, an upsell add, a quantity tap) can land before the note's
           save, and the field has lost focus by then. */
        if (noteField && document.activeElement !== noteField && !noteEdited) {
          noteField.value = cart.note || '';
          if (cart.note) setNoteOpen(true, false);
        }
      }

      renderGift(cart);
      /* Never let the deadline line stop the rest of render() (ship bar,
         upsells, header count). */
      try { renderDeadline(cart); } catch (e) { /* ignore */ }

      if (shipbarEl && config.freeShippingMin > 0) {
        shipbarEl.hidden = false;
        var remaining = config.freeShippingMin - cart.items_subtotal_price;
        var progress = Math.min(100, Math.round(cart.items_subtotal_price * 100 / config.freeShippingMin));
        shipbarProgress.style.width = progress + '%';
        shipbarStatus.textContent = remaining > 0
          ? config.fsRemainingText.replace('[amount]', formatMoney(remaining))
          : config.fsUnlockedText;
      }

      /* Recommendations cost a network round trip, so they only run while the
         drawer is actually on screen. open() renders them for a cart that was
         already loaded. */
      if (isOpen) renderUpsells(cart);
      updateHeaderCount(cart);
    }

    /* ---------- gift options ----------
       Three things, all driven by what is in the cart:

       1. Gift note. When a gift set is in the cart and no line has a Gift
          Message (the "gift-note state", gift options on), the note link
          becomes "Add a free gift note" with a hint, and an empty note gets a
          small nudge above the upsells. When a line already has a Gift
          Message (it renders read-only on the line), the note keeps its plain
          label: that message is the one printed. Packing slips never show
          prices.

       2. Packaging choice, saved as the cart attribute "Gift packaging"
          (Combined / Separate). Counting rules are in giftSummary(). The
          preselected value (section setting) is written as soon as the prompt
          is on screen, so the stored value always matches what the shopper
          saw; dropping below the trigger writes "" to clear it. Every write is
          one /cart/update.js {attributes} call through the queue. Only when
          the section's packaging prompt setting is on (it renders the
          fieldset); without the fieldset none of this runs.

       3. "Gift note" cart attribute, the part that makes the promise true:
          MarkingOrders prints the ORDER NOTE on a gift card only when the
          order's attribute `Gift note` is `Yes` and a gift set on the order
          still ships (drawer orders carry no Gift line property). It rides in
          the same /cart/update.js call that saves the note: `Yes` when the
          note is non-empty and the drawer is in the gift-note state, ""
          (which removes it) when the note is saved empty or a line carries a
          Gift Message, and left as it is otherwise (no gift set in the cart
          right now). So a note saved with a gift set keeps its Yes while the
          set is removed and another added, and a note written before any gift
          set stays plain. Removing the gift set never clears it (MarkingOrders
          ignores a Yes without a shipping gift set); syncGiftNote() clears it
          with its own queued write only when the note is emptied elsewhere or
          a line gains a Gift Message. Checkout waits for both. Key and value
          are a contract with MarkingOrders: never rename one without changing
          it there too. */
    var PACK_ATTR = 'Gift packaging';
    var PACK_VALUES = ['Combined', 'Separate'];

    function lower(value) { return String(value || '').toLowerCase(); }

    function isGiftLine(item) {
      var type = lower(item.product_type);
      return lower(item.vendor) === 'gift set' || type === 'gift set' || type === 'bundle';
    }

    /* Which lines count, read only from fields /cart.js returns:
       - a bottle inside a builder box (_box_id, not the parent handle) is part
         of its box: the box parent counts once, even though gift-box is $0
       - skipped entirely: gift cards (gift_card / type "Gift Card"), anything
         that does not ship (requires_shipping false), the subscription
         (selling_plan_allocation / type "Subscription"), type "Freebie"
         (stickers, flake and candy samples, caps, cozies) and _freebottle
         promo lines
       - skipped from the counts: any other line whose final price is $0
         (a freebie zeroed by a discount)
       - gift unit: vendor "Gift Set" or type "Gift Set"/"Bundle", by quantity;
         every other line is an "other" unit, by quantity. */
    function giftSummary(cart) {
      var s = { giftUnits: 0, otherUnits: 0, hasGift: false, hasGiftMessage: false, packaging: false };
      ((cart && cart.items) || []).forEach(function (item) {
        var props = item.properties || {};
        var type = lower(item.product_type);
        if (String(props['Gift Message'] || '').trim()) s.hasGiftMessage = true;
        var isBoxParent = config.boxParentHandles.indexOf(item.handle) !== -1;
        if (props._box_id && !isBoxParent) return;
        if (item.gift_card || type === 'gift card') return;
        if (item.requires_shipping === false) return;
        if (item.selling_plan_allocation || type === 'subscription') return;
        if (type === 'freebie' || props._freebottle) return;
        var gift = isGiftLine(item);
        if (gift) s.hasGift = true;
        if (item.final_line_price === 0 && !isBoxParent) return;
        if (gift) {
          s.giftUnits += item.quantity;
        } else {
          s.otherUnits += item.quantity;
        }
      });
      s.packaging = s.giftUnits >= 2 || (s.giftUnits >= 1 && s.otherUnits >= 2);
      return s;
    }

    /* Holiday deadline eligibility: at least one line that ships and is not
       the subscription, a gift card, a freebie or a _freebottle promo line
       (bottles inside a builder box count; they ship). Read-only: no cart
       writes, nothing queued. */
    function deadlineEligible(cart) {
      return ((cart && cart.items) || []).some(function (item) {
        var props = item.properties || {};
        var type = lower(item.product_type);
        if (item.gift_card || type === 'gift card') return false;
        if (item.requires_shipping === false) return false;
        if (item.selling_plan_allocation || type === 'subscription') return false;
        if (type === 'freebie' || props._freebottle) return false;
        return true;
      });
    }

    function renderDeadline(cart) {
      if (!deadlineEl) return;
      deadlineEl.setAttribute('data-hd-eligible', deadlineEligible(cart) ? '1' : '0');
      if (window.HeatDeadlines) window.HeatDeadlines.refresh();
    }

    function storedPackaging(cart) {
      var value = (cart && cart.attributes && cart.attributes[PACK_ATTR]) || '';
      return PACK_VALUES.indexOf(value) !== -1 ? value : '';
    }

    function renderGift(cart) {
      if (!cart) return;
      var s = giftSummary(cart);
      var noteIsGift = s.hasGift && !s.hasGiftMessage;

      if (noteLabel) {
        noteLabel.textContent = noteLabel.getAttribute(noteIsGift && giftEl ? 'data-label-gift' : 'data-label-default');
      }
      if (noteHint) noteHint.hidden = !(noteIsGift && giftEl);
      if (noteField) {
        /* an older cached section has no data-placeholder-*: keep its own */
        var placeholder = noteField.getAttribute(noteIsGift && giftEl ? 'data-placeholder-gift' : 'data-placeholder-default');
        if (placeholder !== null) noteField.placeholder = placeholder;
      }

      /* before the giftEl bail-out: an emptied note or a new Gift Message line
         clears the attribute even with gift options switched off */
      syncGiftNote();

      if (!giftEl) return;

      var noteEmpty = !String(cart.note || '').trim() && !(noteField && noteField.value.trim());
      var noteOpen = noteBody && !noteBody.hidden;
      if (nudgeEl) nudgeEl.hidden = !(noteIsGift && noteEmpty && !noteOpen && noteWrap);

      if (packEl) {
        packEl.hidden = !s.packaging;
        if (s.packaging) {
          var shown = packUserChoice || storedPackaging(cart) || config.giftPackagingDefault;
          setPackChecked(shown);
        }
      }
      giftEl.hidden = (!nudgeEl || nudgeEl.hidden) && (!packEl || packEl.hidden);

      syncPackaging();
    }

    function setPackChecked(value) {
      for (var i = 0; i < packInputs.length; i++) {
        var input = packInputs[i];
        input.checked = input.value === value;
        var label = input.closest('label');
        if (label) label.classList.toggle('is-selected', input.checked);
      }
    }

    /* What the attribute should become right now, or null for "leave it".
       The default is only written while the drawer is open, i.e. once the
       shopper can actually see the preselected choice. Clearing is always
       safe, so it happens whenever the cart drops below the trigger. */
    function wantedPackaging(cart) {
      if (!cart || !packEl) return null;
      var raw = (cart.attributes && cart.attributes[PACK_ATTR]) || '';
      if (!giftSummary(cart).packaging) return raw ? '' : null;
      if (storedPackaging(cart)) return null;
      if (!isOpen) return null;
      return config.giftPackagingDefault === 'Separate' ? 'Separate' : 'Combined';
    }

    /* Automatic writes (default on first show, clear on drop). Safe to call
       from render(), including inside a queued task: the write is chained
       after the current task and never awaited by it. */
    function syncPackaging() {
      if (packPending > 0) return;
      var want = wantedPackaging(currentCart);
      if (want === null || want === packFailedValue) return;
      writePackaging(want, false);
    }

    function writePackaging(value, fromUser) {
      packPending++;
      return enqueue(function () {
        /* an automatic write re-checks against the cart at execution time;
           a shopper's tap is dropped if the cart fell below the trigger
           while it waited in the queue (the prompt is gone) */
        if (!fromUser && wantedPackaging(currentCart) !== value) return;
        if (fromUser && !giftSummary(currentCart).packaging) {
          packUserChoice = null;
          return;
        }
        var attributes = {};
        attributes[PACK_ATTR] = value;
        internalMutations++;
        return window.HeatCart.updateCart({ attributes: attributes })
          .then(function (response) {
            if (!response.ok) throw new Error('packaging save failed');
            return response.json();
          })
          .then(function (cart) {
            packFailedValue = null;
            packUserChoice = null;
            render(cart);
          })
          .catch(function () {
            packUserChoice = null;
            if (fromUser) {
              showError();
            } else {
              packFailedValue = value;
            }
            return refresh().catch(function () {});
          })
          .then(function () { internalMutations--; });
      }).then(function () {
        packPending--;
        /* renders inside these writes skipped syncPackaging (packPending > 0):
           catch up once the last one is done. Terminates: the next write only
           happens if the cart still disagrees, and a failed automatic value is
           never retried (packFailedValue). */
        if (packPending === 0) syncPackaging();
      });
    }

    if (packEl) {
      on(packEl, 'change', function (event) {
        var input = event.target.closest('[data-slidecart-pack-input]');
        if (!input || !input.checked) return;
        packUserChoice = input.value;
        setPackChecked(input.value);
        writePackaging(input.value, true);
      });
    }

    /* "Gift note" attribute (paragraph 3 above). The note save sends it;
       syncGiftNote() only ever clears it. */
    var GIFT_NOTE_ATTR = 'Gift note';
    var giftNotePending = 0;      /* clearing writes queued or in flight */
    var giftNoteFailed = false;   /* a clear that failed: never retried in a loop */

    /* What a note save of `value` sends with the note: 'Yes' in the gift-note
       state (a gift set in the cart, no Gift Message line, gift options on),
       '' to remove it for an empty note or when a line carries a Gift
       Message, and null to leave the attribute as it is (no gift set in the
       cart right now, or gift options off). */
    function giftNoteFor(cart, value) {
      if (!String(value || '').trim()) return '';
      var s = giftSummary(cart);
      if (s.hasGiftMessage) return '';
      if (giftEl && s.hasGift) return 'Yes';
      return null;
    }

    /* true when the stored attribute must go: the saved note is empty, or a
       line now carries a Gift Message (that message gets the card). Never
       because the gift set left the cart. */
    function giftNoteStale(cart) {
      if (!cart || !(cart.attributes && cart.attributes[GIFT_NOTE_ATTR])) return false;
      return giftNoteFor(cart, cart.note) === '';
    }

    /* Automatic clear. Safe to call from render(), including inside a queued
       task: the write is chained after the current task and never awaited by
       it, and it re-checks the cart when it runs. */
    function syncGiftNote() {
      if (giftNotePending > 0 || giftNoteFailed || !giftNoteStale(currentCart)) return;
      giftNotePending++;
      enqueue(function () {
        if (!giftNoteStale(currentCart)) return;
        var attributes = {};
        attributes[GIFT_NOTE_ATTR] = '';
        internalMutations++;
        return window.HeatCart.updateCart({ attributes: attributes })
          .then(function (response) {
            if (!response.ok) throw new Error('gift note clear failed');
            return response.json();
          })
          .then(function (cart) { render(cart); })
          .catch(function () {
            giftNoteFailed = true;
            return refresh().catch(function () {});
          })
          .then(function () { internalMutations--; });
      }).then(function () {
        giftNotePending--;
        /* renders inside the write skipped this (giftNotePending > 0) */
        if (giftNotePending === 0) syncGiftNote();
      });
    }

    if (nudgeEl) {
      on(nudgeEl, 'click', function (event) {
        if (!event.target.closest('[data-slidecart-gift-nudge-open]')) return;
        setNoteOpen(true, true);
        if (noteWrap && noteWrap.scrollIntoView) {
          noteWrap.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });
    }

    /* ---------- upsells: Shopify native product recommendations ---------- */
    /* Seeds are the newest few distinct cart products (cart.js lists the most
       recently added line first). For each seed, ask for the complementary
       list, then the related list. Search & Discovery's manual lists lead both
       (Inventory writes them monthly, Inventory/recommendations.py); Shopify's
       own picks fill the rest.
       Candidates are interleaved across seeds, complementary before related,
       then filtered to cheap add-ons: never a freebie, sample, gift card or the
       subscription, never above upsellMaxPrice, never already in the cart.
       Adds carry the hidden line property _upsell=cart for attribution. Free
       shipping (giftfreeship) is US only: the heading names the US only when
       the visitor is detected outside it, see visitorCountry(). */
    var upsellsSection = root.querySelector('[data-slidecart-upsells]');
    var upsellList = root.querySelector('[data-slidecart-upsell-list]');
    var upsellHeading = root.querySelector('[data-slidecart-upsell-heading]');
    var recsCache = {};
    var freeShipCache = {};
    var upsellRenderSeq = 0;

    function fetchRecs(productId, intent) {
      var key = intent + ':' + productId;
      if (!recsCache[key]) {
        recsCache[key] = fetch('/recommendations/products.json?product_id=' + productId +
          '&limit=10&intent=' + intent, { headers: { 'Accept': 'application/json' } })
          .then(function (r) {
            if (!r.ok) throw new Error('recommendations ' + r.status);
            return r.json();
          })
          .then(function (data) { return data.products || []; })
          .catch(function () {
            delete recsCache[key];
            return [];
          });
      }
      return recsCache[key];
    }

    /* cart.js carries no tags, so look up the giftfreeship tag (whole order
       ships free) on the product JSON, only for gift set and bundle lines. */
    function shipsFree(item) {
      if (config.upsellFreeShipTypes.indexOf(item.product_type) === -1) return Promise.resolve(false);
      var handle = item.handle;
      if (!freeShipCache[handle]) {
        freeShipCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
          headers: { 'Accept': 'application/json' }
        })
          .then(function (r) {
            if (!r.ok) throw new Error('product ' + r.status);
            return r.json();
          })
          .then(function (p) { return (p.tags || []).indexOf(config.upsellFreeShipTag) !== -1; })
          .catch(function () {
            delete freeShipCache[handle];
            return false;
          });
      }
      return freeShipCache[handle];
    }

    /* The visitor's IP country from Shopify's browsing-context endpoint (the
       same signal its geolocation app uses), asked only when a cart ships
       free, and remembered for the browser session. Resolves '' when unknown. */
    var COUNTRY_KEY = 'heatSlidecartCountry';
    var countryPromise = null;
    function visitorCountry() {
      if (countryPromise) return countryPromise;
      var stored = '';
      try { stored = window.sessionStorage.getItem(COUNTRY_KEY) || ''; } catch (e) {}
      if (stored) {
        countryPromise = Promise.resolve(stored);
        return countryPromise;
      }
      countryPromise = fetch('/browsing_context_suggestions.json', { headers: { 'Accept': 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('browsing context ' + r.status);
          return r.json();
        })
        .then(function (data) {
          var c = data && data.detected_values && data.detected_values.country;
          var code = c && c.handle ? String(c.handle).toUpperCase() : '';
          if (code) {
            try { window.sessionStorage.setItem(COUNTRY_KEY, code); } catch (e) {}
          }
          return code;
        })
        .catch(function () {
          countryPromise = null;
          return '';
        });
      return countryPromise;
    }

    function interleave(lists) {
      var out = [];
      var longest = 0;
      lists.forEach(function (l) { longest = Math.max(longest, l.length); });
      for (var i = 0; i < longest; i++) {
        lists.forEach(function (l) { if (i < l.length) out.push(l[i]); });
      }
      return out;
    }

    function renderUpsellCard(product) {
      var li = el('li', 'heat-slidecart__upsell-card');
      li.setAttribute('data-upsell-product-id', product.id);

      var media = el('a', 'heat-slidecart__upsell-media');
      media.href = product.url;
      media.tabIndex = -1;
      media.setAttribute('aria-hidden', 'true');
      if (product.featured_image) {
        var img = document.createElement('img');
        img.src = imageUrl(product.featured_image, 160);
        img.alt = '';
        img.width = 80;
        img.height = 80;
        img.loading = 'lazy';
        media.appendChild(img);
      }
      li.appendChild(media);

      var info = el('div', 'heat-slidecart__upsell-info');
      var title = el('a', 'heat-slidecart__upsell-title', product.title);
      title.href = product.url;
      info.appendChild(title);
      var price = el('span', 'heat-slidecart__upsell-price');
      if (product.compare_at_price && product.compare_at_price > product.price) {
        price.appendChild(el('s', null, formatMoney(product.compare_at_price)));
        price.appendChild(document.createTextNode(' '));
      }
      price.appendChild(document.createTextNode(formatMoney(product.price)));
      info.appendChild(price);
      li.appendChild(info);

      var variants = product.variants || [];
      if (variants.length === 1 && variants[0].available !== false) {
        var btn = el('button', 'heat-slidecart__upsell-add', '+ Add');
        btn.type = 'button';
        btn.setAttribute('data-upsell-add', '');
        btn.setAttribute('data-variant-id', variants[0].id);
        btn.setAttribute('data-product-title', product.title);
        btn.setAttribute('aria-label', 'Add ' + product.title + ' to cart');
        li.appendChild(btn);
      } else {
        var link = el('a', 'heat-slidecart__upsell-add heat-slidecart__upsell-add--link', 'Options');
        link.href = product.url;
        link.setAttribute('aria-label', 'View options for ' + product.title);
        li.appendChild(link);
      }
      return li;
    }

    function renderUpsells(cart) {
      if (!upsellsSection || !upsellList) return;
      var seq = ++upsellRenderSeq;
      if (!cart || cart.item_count === 0) {
        upsellsSection.hidden = true;
        return;
      }
      var excluded = config.upsellExcludeTypes;
      var inCart = {};
      var seeds = [];
      cart.items.forEach(function (item) {
        inCart[item.product_id] = true;
      });
      /* Until the fresh list arrives, drop any card already in the cart (just
         added from the drawer, or added elsewhere while it was closed), so a
         second tap can never add a duplicate line. */
      Array.prototype.forEach.call(upsellList.querySelectorAll('[data-upsell-product-id]'), function (card) {
        if (inCart[card.getAttribute('data-upsell-product-id')]) card.remove();
      });
      if (!upsellList.children.length) upsellsSection.hidden = true;
      cart.items.forEach(function (item) {
        if (seeds.length < config.upsellSeedCount &&
            seeds.indexOf(item.product_id) === -1 &&
            excluded.indexOf(item.product_type) === -1) {
          seeds.push(item.product_id);
        }
      });
      if (!seeds.length) {
        upsellsSection.hidden = true;
        return;
      }

      var lookups = [];
      seeds.forEach(function (id) { lookups.push(fetchRecs(id, 'complementary')); });
      seeds.forEach(function (id) { lookups.push(fetchRecs(id, 'related')); });

      Promise.all([
        Promise.all(lookups),
        Promise.all(cart.items.map(shipsFree)).then(function (flags) {
          if (!flags.some(Boolean)) return null;
          return visitorCountry();
        })
      ]).then(function (results) {
        if (seq !== upsellRenderSeq) return; /* a newer cart has rendered since */
        var lists = results[0];
        var candidates = interleave(lists.slice(0, seeds.length))
          .concat(interleave(lists.slice(seeds.length)));
        var seen = {};
        var visible = candidates.filter(function (p) {
          if (seen[p.id] || inCart[p.id]) return false;
          seen[p.id] = true;
          return p.available !== false &&
            excluded.indexOf(p.type) === -1 &&
            p.price > 0 &&
            p.price <= config.upsellMaxPrice;
        }).slice(0, 3);
        if (!visible.length) {
          upsellsSection.hidden = true;
          return;
        }
        if (upsellHeading) {
          /* results[1]: null = nothing ships free; '' = country unknown, which
             keeps the short US copy; any other code outside the home country
             gets the version that says where shipping is free. */
          var country = results[1];
          var heading = config.upsellHeading;
          if (country !== null) {
            heading = (country && country !== config.upsellHomeCountry)
              ? config.upsellHeadingFreeShipIntl
              : config.upsellHeadingFreeShip;
          }
          upsellHeading.textContent = heading;
        }
        upsellList.textContent = '';
        visible.forEach(function (p) { upsellList.appendChild(renderUpsellCard(p)); });
        upsellsSection.hidden = false;
      }).catch(function () {
        if (seq === upsellRenderSeq) upsellsSection.hidden = true;
      });
    }

    function updateHeaderCount(cart) {
      document.querySelectorAll('[data-header-cart-count]').forEach(function (node) {
        node.setAttribute('data-header-cart-count', cart.item_count > 0 ? String(cart.item_count) : '');
        node.classList.toggle('visible', cart.item_count > 0);
      });
      /* existing event vocabulary — gift-set-showcase re-syncs on this */
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
    }

    /* Raw /cart.js read. Call it directly from inside a queued task; use
       enqueueRead(refresh) from anywhere else. */
    function refresh() {
      return window.HeatCart.getCart().then(function (cart) {
        if (cart) render(cart);
        return cart;
      });
    }

    function scheduleRefresh(thenOpen) {
      pendingOpen = pendingOpen || !!thenOpen;
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(function () {
        enqueueRead(function () {
          return refresh().then(function () {
            if (pendingOpen) open();
            pendingOpen = false;
          }).catch(function () { pendingOpen = false; });
        });
      }, 150);
    }

    /* ---------- open / close ---------- */
    function open(skipPackSync) {
      if (isOpen) return;
      if (document.body.classList.contains('subscription-checkout-redirect')) return;
      isOpen = true;
      lastFocused = document.activeElement;
      root.classList.add('is-open');
      document.body.classList.add('heat-slidecart-locked');
      errorEl.hidden = true;
      closeBtn.focus();
      /* upsells are skipped while closed, so catch up for an already-loaded cart */
      if (currentCart) renderUpsells(currentCart);
      /* the packaging prompt is now on screen: store its preselected value.
         openAndRefresh skips this and lets its fresh render do it instead of
         acting on a possibly stale cart. */
      if (currentCart && !skipPackSync) syncPackaging();
    }

    /* Opening is the first moment the drawer needs cart state: nothing fetches
       /cart.js on page load any more (the header bubble is server-rendered). */
    function openAndRefresh() {
      open(true);
      enqueueRead(function () { return refresh().catch(function () {}); });
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      upsellRenderSeq++; /* an upsell fetch still in flight belongs to the old cart */
      root.classList.remove('is-open');
      document.body.classList.remove('heat-slidecart-locked');
      if (lastFocused && document.contains(lastFocused)) lastFocused.focus();
    }

    function gestureRecent() {
      return (Date.now() - window.__heatSlideCartLastGesture) < config.gestureWindowMs;
    }

    /* ---------- order note ---------- */
    function setNoteOpen(shouldOpen, focus) {
      if (!noteBody || !noteToggle) return;
      noteBody.hidden = !shouldOpen;
      noteToggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      if (shouldOpen && focus && noteField) noteField.focus();
      if (nudgeEl && currentCart) renderGift(currentCart);
    }

    /* Saves the note and, in the same call, the "Gift note" attribute when
       giftNoteFor() has a value for it (see the gift options comment).
       Resolves true once saved, false if the save failed. */
    function saveNote() {
      if (!noteField) return Promise.resolve(true);
      var saved = false;
      return enqueue(function () {
        var value = noteField.value;
        var giftNote = giftNoteFor(currentCart, value);
        var payload = { note: value };
        if (giftNote !== null) {
          payload.attributes = {};
          payload.attributes[GIFT_NOTE_ATTR] = giftNote;
        }
        noteEdited = false;
        internalMutations++;
        return window.HeatCart.updateCart(payload)
          .then(function (response) {
            if (!response.ok) throw new Error('note save failed');
            saved = true;
            giftNoteFailed = false;
            return response.json().catch(function () { return null; });
          })
          .then(function (cart) {
            if (currentCart) {
              currentCart.note = value;
              if (cart && cart.attributes) {
                currentCart.attributes = cart.attributes;
              } else if (giftNote !== null) {
                currentCart.attributes = currentCart.attributes || {};
                if (giftNote) {
                  currentCart.attributes[GIFT_NOTE_ATTR] = giftNote;
                } else {
                  delete currentCart.attributes[GIFT_NOTE_ATTR];
                }
              }
            }
            if (noteStatus) noteStatus.textContent = 'Note saved';
          })
          .catch(function () {
            /* keep the shopper's text on screen; the next keystroke retries */
            if (noteField.value === value) noteEdited = true;
            if (noteStatus) noteStatus.textContent = "Couldn't save your note. Please try again.";
          })
          .then(function () { internalMutations--; });
      }).then(function () { return saved; });
    }

    if (noteToggle) {
      on(noteToggle, 'click', function () {
        setNoteOpen(noteBody.hidden, true);
      });
    }
    if (noteField) {
      on(noteField, 'input', function () {
        noteEdited = true;
        clearTimeout(noteTimer);
        if (noteStatus) noteStatus.textContent = '';
        noteTimer = setTimeout(saveNote, 700);
      });
    }
    if (checkoutForm) {
      /* flush an unsaved note, and let any queued gift-packaging or gift-note
         write land, before leaving for checkout. A changed note that still did
         not save holds checkout: leaving would drop the new text, or keep a
         note the shopper deleted (still flagged for a gift card). */
      on(checkoutForm, 'submit', function (event) {
        var savedNote = (currentCart && currentCart.note) || '';
        var noteDirty = !!noteField && noteField.value !== savedNote;
        if (!noteDirty && packPending === 0 && giftNotePending === 0) return;
        event.preventDefault();
        clearTimeout(noteTimer);
        var flush = noteDirty ? saveNote() : enqueueRead(function () {});
        flush.then(function (saved) {
          if (saved === false) {
            setNoteOpen(true, false);
            showError(noteField.value.trim()
              ? "Your note didn't save. Tap Checkout again to retry, or clear the note to check out without it."
              : "Your note change didn't save. Tap Checkout again to retry.");
            return;
          }
          /* form.submit() skips the button's name=checkout pair — re-add it */
          var flag = document.createElement('input');
          flag.type = 'hidden';
          flag.name = 'checkout';
          flag.value = '';
          checkoutForm.appendChild(flag);
          checkoutForm.submit();
        });
      });
    }

    /* ---------- cart mutations from inside the drawer ----------
       Clearing data-busy is belt and braces: on the success path render() has
       already thrown the dimmed node away, but a failed request that also
       fails to refresh would otherwise leave a line stuck dim forever. Clear
       the node we dimmed AND whatever line now holds that key. */
    function clearLineBusy(key, node) {
      if (node) node.removeAttribute('data-busy');
      var live = linesEl.querySelector('[data-line-key="' + key + '"]');
      if (live) live.removeAttribute('data-busy');
    }

    /* Quantity work is queued as a DELTA (+1 / -1), or an explicit 0 to remove
       the line, never as an absolute quantity. The target is computed from
       `currentCart` at execution time, so a second tap that landed while the
       first request was in flight builds on the first result instead of
       racing it or being dropped: two fast "+" taps mean two sequential
       /cart/change.js calls ending at +2. */
    function mutateLineBy(key, delta) {
      return enqueue(function () {
        var item = findItem(key);
        if (!item) return; /* line is already gone — no request to make */
        var quantity = delta === 0 ? 0 : Math.max(0, item.quantity + delta);
        var line = linesEl.querySelector('[data-line-key="' + key + '"]');
        if (line) line.setAttribute('data-busy', '');
        internalMutations++;
        return window.HeatCart.changeLine({ id: key, quantity: quantity })
          .then(function (response) {
            if (!response.ok) return failWith(response, 'change failed');
            return response.json();
          })
          .then(function (cart) {
            render(cart);
            announceEl.textContent = 'Cart updated.';
          })
          .catch(function (error) {
            showError(error && error.cartMessage);
            return refresh().catch(function () {});
          })
          .then(function () {
            internalMutations--;
            clearLineBusy(key, line);
          });
      });
    }

    function removeBox(boxLine) {
      var boxKeys = (boxLine.getAttribute('data-box-keys') || '').split('|').filter(Boolean);
      if (!boxKeys.length) return;
      boxLine.setAttribute('data-busy', '');
      return enqueue(function () {
        var boxUpdates = {};
        boxKeys.forEach(function (key) { boxUpdates[key] = 0; });
        internalMutations++;
        return window.HeatCart.updateCart({ updates: boxUpdates })
          .then(function (response) {
            if (!response.ok) throw new Error('box remove failed');
            return response.json();
          })
          .then(function (cart) {
            render(cart);
            announceEl.textContent = 'Gift box removed.';
          })
          .catch(function () {
            showError();
            return refresh().catch(function () {});
          })
          .then(function () {
            internalMutations--;
            /* usually detached by render() already; clears the dim if the
               request failed and the same box line is still on screen */
            boxLine.removeAttribute('data-busy');
          });
      });
    }

    function addUpsell(upsellBtn) {
      upsellBtn.dataset.busy = '1';
      var originalLabel = upsellBtn.textContent;
      upsellBtn.textContent = 'Adding…';
      return enqueue(function () {
        internalMutations++;
        return window.HeatCart.addToCart({ items: [{
          id: Number(upsellBtn.getAttribute('data-variant-id')),
          quantity: 1,
          properties: { _upsell: 'cart' }
        }] })
          .then(function (response) {
            if (!response.ok) return failWith(response, 'add failed');
            announceEl.textContent = upsellBtn.getAttribute('data-product-title') + ' added to cart.';
            return refresh();
          })
          .catch(function (error) {
            showError(error && error.cartMessage);
          })
          .then(function () {
            internalMutations--;
            upsellBtn.textContent = originalLabel;
            delete upsellBtn.dataset.busy;
          });
      });
    }

    /* ---------- events ---------- */
    on(root, 'click', function (event) {
      if (event.target === overlay || event.target.closest('[data-slidecart-close]')) {
        close();
        return;
      }

      /* No busy guard on any of these: a click during an in-flight request is
         queued behind it, never thrown away. The queue keeps the calls
         sequential. */
      var boxRemoveBtn = event.target.closest('[data-box-remove]');
      if (boxRemoveBtn) {
        var boxLine = boxRemoveBtn.closest('[data-box-keys]');
        if (boxLine) removeBox(boxLine);
        return;
      }

      var qtyBtn = event.target.closest('[data-qty-change]');
      if (qtyBtn) {
        var line = qtyBtn.closest('[data-line-key]');
        if (line) {
          mutateLineBy(line.getAttribute('data-line-key'), Number(qtyBtn.getAttribute('data-qty-change')));
        }
        return;
      }

      var removeBtn = event.target.closest('[data-line-remove]');
      if (removeBtn) {
        var removeLine = removeBtn.closest('[data-line-key]');
        if (removeLine) mutateLineBy(removeLine.getAttribute('data-line-key'), 0);
        return;
      }

      var upsellBtn = event.target.closest('[data-upsell-add]');
      if (upsellBtn && !upsellBtn.dataset.busy) {
        addUpsell(upsellBtn);
      }
    });

    function findItem(key) {
      if (!currentCart) return null;
      for (var i = 0; i < currentCart.items.length; i++) {
        if (currentCart.items[i].key === key) return currentCart.items[i];
      }
      return null;
    }

    onDoc('keydown', function (event) {
      if (!isOpen) return;
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key === 'Tab') {
        var focusables = panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        var visible = [];
        focusables.forEach(function (node) {
          if (node.offsetParent !== null || node === document.activeElement) visible.push(node);
        });
        if (!visible.length) return;
        var first = visible[0];
        var last = visible[visible.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    /* header cart icon opens the drawer (except on the cart page itself;
       modified clicks keep native navigation) */
    onDoc('click', function (event) {
      if (config.isCartPage) return;
      var cartLink = event.target.closest('.site-header-cart--button');
      if (!cartLink) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      openAndRefresh();
    });

    /* observed cart traffic (Empire bundle + HeatCart sections) */
    onDoc('heat:slidecart:cart-request', function (event) {
      if (internalMutations > 0) return; /* drawer's own calls handle their render */
      var isAdd = event.detail && event.detail.action === 'add';
      scheduleRefresh(isAdd && gestureRecent());
    });

    /* HeatCart's own hook: same story as the network observer above, and the
       150ms debounce in scheduleRefresh coalesces the pair into one fetch. */
    onDoc('heat:cart:mutated', function (event) {
      if (internalMutations > 0) return;
      var detail = event.detail || {};
      if (detail.ok === false) return;
      var isAdd = String(detail.endpoint || '').indexOf('/cart/add.js') !== -1;
      scheduleRefresh(isAdd && gestureRecent());
    });

    /* bfcache restore: the page comes back with whatever cart it had when the
       customer navigated away, so re-read once. */
    on(window, 'pageshow', function (event) {
      if (!event.persisted) return;
      enqueueRead(function () { return refresh().catch(function () {}); });
    });

    /* theme editor support */
    if (window.Shopify && window.Shopify.designMode) {
      onDoc('shopify:section:select', function (event) {
        if (event.target.contains(root) || event.target.querySelector('[data-heat-slidecart-root]')) {
          openAndRefresh();
        }
      });
      onDoc('shopify:section:deselect', function () { close(); });
    }

    window.__heatSlideCart = {
      open: openAndRefresh,
      close: close,
      refresh: function () {
        return enqueueRead(function () { return refresh(); });
      },
      destroy: function () {
        close();
        clearTimeout(refreshTimer);
        clearTimeout(noteTimer);
        listeners.forEach(function (entry) {
          entry[0].removeEventListener(entry[1], entry[2]);
        });
        listeners.length = 0;
      }
    };

    /* No /cart.js on page load: the header count bubble is server-rendered by
       static-header.liquid, and the drawer loads its cart on first open. */
  }

  function isOurSection(event) {
    var id = event && event.detail && event.detail.sectionId;
    if (id != null && currentSectionId != null && String(id) === currentSectionId) return true;
    var target = event && event.target;
    return !!(target && target.querySelector && target.querySelector('[data-heat-slidecart-root]'));
  }

  /* The section markup can arrive after this deferred file has run (theme
     editor re-renders inject HTML, which never re-executes an external
     script), so re-init on section load and tear down on unload. */
  document.addEventListener('shopify:section:load', function (event) {
    if (!isOurSection(event)) return;
    init();
  });
  document.addEventListener('shopify:section:unload', function (event) {
    if (!isOurSection(event)) return;
    if (window.__heatSlideCart && window.__heatSlideCart.destroy) {
      window.__heatSlideCart.destroy();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
